import type { Key } from "react-stately";

import { Outlet, useRouter } from "@tanstack/react-router";
import { ChevronLeftIcon } from "lucide-react";

import { Button } from "@/components/button";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";

interface BillingShellProps {
  readonly accountName: string;
  readonly onTabChange: (key: Key) => void;
  readonly selectedTab: string;
  readonly tabs: readonly { id: string; label: string }[];
}

export function BillingShell({
  accountName,
  onTabChange,
  selectedTab,
  tabs,
}: BillingShellProps) {
  const { history } = useRouter();

  return (
    <div className="relative bg-bg-tertiary sm:p-xl">
      <div className="mx-auto flex w-full flex-col">
        <div className="flex items-start gap-2xl sm:rounded-t-xl sm:border-b sm:border-b-border-secondary bg-bg-primary pt-xl sm:pb-2xl px-3xl">
          <Button
            className="mt-xs shrink-0"
            color="gray"
            isIconOnly
            onPress={() => history.go(-1)}
            size="lg"
            variant="secondary"
          >
            <ChevronLeftIcon size={20} />
          </Button>
          <div className="flex flex-1 flex-wrap items-center justify-between gap-y-lg">
            <span className="text-2xl font-semibold text-text-primary">
              {accountName}
            </span>
            <Button size="lg">Change subscription</Button>
          </div>
        </div>

        <Tabs
          className="rounded-b-xl bg-bg-primary"
          onSelectionChange={onTabChange}
          selectedKey={selectedTab}
        >
          <div className="overflow-x-auto scrollbar-none sm:overflow-x-visible border-b border-b-border-secondary px-3xl pt-2xl">
            <TabList
              aria-label="Billing Management"
              className="flex min-w-full flex-nowrap border-b-0 sm:flex-wrap"
            >
              {tabs.map((tab) => (
                <Tab id={tab.id} key={tab.id}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </div>

          {tabs.map((tab) => (
            <TabPanel className="flex-initial py-0" id={tab.id} key={tab.id}>
              {selectedTab === tab.id && <Outlet />}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
